/**
 * Simple in-memory cache for API responses.
 *
 * Keeps gallery data cached so navigating back (e.g., from theme → year page)
 * shows results instantly without refetching from the API. Images then load
 * from the browser's HTTP cache (served by CloudFront with long TTLs).
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

/** Default TTL: 5 minutes (API data doesn't change frequently) */
const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * Get a cached value if it exists and hasn't expired.
 */
export function getCached<T>(key: string, ttlMs = DEFAULT_TTL_MS): T | null {
  const entry = cache.get(key);
  if (!entry) return null;

  if (Date.now() - entry.timestamp > ttlMs) {
    cache.delete(key);
    return null;
  }

  return entry.data as T;
}

/**
 * Store a value in the cache.
 */
export function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Invalidate a specific cache entry.
 */
export function invalidateCache(key: string): void {
  cache.delete(key);
}

/**
 * Clear the entire cache.
 */
export function clearCache(): void {
  cache.clear();
}
